<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">邮件模板管理</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.keyword" placeholder="请输入模板名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增模板
        </el-button>
      </div>

      <!-- 邮件模板列表 -->
      <el-table :data="templateList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="关联用户" width="150">
          <template #default="{ row }">
            <div class="user-info">
              <div class="user-nickname">{{ getUserInfo(row.user_id).nickname }}</div>
              <div class="user-phone">{{ getUserInfo(row.user_id).phone }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="template_name" label="模板名称" width="150" />
        <el-table-column prop="subject" label="邮件主题" width="200" />
        <el-table-column label="邮件内容" min-width="250">
          <template #default="{ row }">
            <div class="content-preview" v-html="truncateHtml(row.content, 100)"></div>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="notes" label="备注" width="150" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="操作" width="380" fixed="right">
          <template #default="scope">
            <el-button type="info" size="small" @click="openPreviewDialog(scope.row)">
              <el-icon>
                <View />
              </el-icon>
              预览
            </el-button>
            <el-button type="warning" size="small" @click="openTestDialog(scope.row)">
              <el-icon>
                <Promotion />
              </el-icon>
              测试
            </el-button>
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchTemplateList" @current-change="fetchTemplateList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="900px" :close-on-click-modal="false">
      <el-form ref="templateForm" :model="templateForm" label-width="120px" :rules="formRules">
        <el-form-item label="模板名称" prop="template_name">
          <el-input v-model="templateForm.template_name" placeholder="请输入模板名称" size="large" />
        </el-form-item>

        <el-form-item label="邮件主题" prop="subject">
          <el-input v-model="templateForm.subject" placeholder="请输入邮件主题" size="large" />
          <div class="form-tip">可使用变量：{name} - 收件人名称</div>
        </el-form-item>

        <el-form-item label="邮件内容" prop="content" class="content-form-item">
          <!-- 工具栏容器 - 始终在上方 -->
          <div class="editor-toolbar">
            <div class="toolbar-left">
              <el-button size="small" @click="openAiGenerateDialog" type="warning">
                <el-icon>
                  <MagicStick />
                </el-icon>
                AI生成模板
              </el-button>
              <el-button size="small" @click="openAiPolishDialog" type="success" v-if="templateForm.content">
                <el-icon>
                  <Brush />
                </el-icon>
                AI润色优化
              </el-button>
            </div>
            <div class="toolbar-right">
              <el-button size="small" @click="showPreview = !showPreview" :type="showPreview ? 'warning' : 'info'">
                <el-icon>
                  <View />
                </el-icon>
                {{ showPreview ? '切换为代码编辑' : '切换为可视化预览' }}
              </el-button>
            </div>
          </div>

          <!-- 编辑模式（代码编辑） -->
          <div v-if="!showPreview" class="code-editor-wrapper">
            <div class="code-editor-header">
              <el-icon>
                <Edit />
              </el-icon>
              <span>HTML代码编辑模式 - 可直接编写或粘贴HTML代码</span>
            </div>
            <el-input v-model="templateForm.content" placeholder="请输入HTML邮件内容，支持HTML标签和内联样式" type="textarea" :rows="12"
              class="content-editor" />
          </div>

          <!-- 预览模式（可视化预览） -->
          <div v-else class="content-preview-box">
            <div class="preview-toolbar">
              <el-tag size="small" type="success" effect="dark">可视化预览</el-tag>
              <span class="preview-hint">这是邮件的实际显示效果，变量 {name} 将替换为收件人名称</span>
            </div>
            <div class="preview-frame" v-html="previewContent"></div>
          </div>

          <!-- 底部提示 -->
          <div class="form-tip">
            <el-icon style="vertical-align: middle;">
              <InfoFilled />
            </el-icon>
            支持HTML格式邮件模板，可使用变量：{name} - 收件人名称。建议使用内联样式以确保邮件客户端兼容性。
          </div>
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="templateForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="templateForm.notes" placeholder="请输入备注信息，如模板用途说明" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTemplateForm">确认</el-button>
      </template>
    </el-dialog>

    <!-- AI生成模板对话框 -->
    <el-dialog v-model="aiGenerateDialogVisible" title="AI生成邮件模板" width="700px" :close-on-click-modal="false">
      <el-form ref="aiGenerateForm" :model="aiGenerateForm" label-width="100px">
        <el-form-item label="需求描述" prop="requirement">
          <el-input v-model="aiGenerateForm.requirement" type="textarea" :rows="6"
            placeholder="请描述您需要的邮件模板，例如：&#10;生成一个商务合作邀请邮件模板，要求简洁大方，包含公司介绍、合作意向、联系方式等信息。使用蓝色和白色为主色调，适合企业使用。" />
        </el-form-item>

        <el-form-item label="提示词参考">
          <div class="prompt-suggestions">
            <el-tag v-for="prompt in promptSuggestions" :key="prompt" class="prompt-tag"
              @click="aiGenerateForm.requirement = prompt" type="info" effect="plain">
              {{ prompt }}
            </el-tag>
          </div>
        </el-form-item>

        <el-form-item label="生成结果" v-if="aiGeneratedContent">
          <div class="generated-content-box">
            <div class="generated-toolbar">
              <el-button size="small" @click="showAiPreview = !showAiPreview"
                :type="showAiPreview ? 'warning' : 'info'">
                <el-icon>
                  <View />
                </el-icon>
                {{ showAiPreview ? '查看HTML代码' : '可视化预览效果' }}
              </el-button>
            </div>

            <!-- 代码模式 -->
            <div v-if="!showAiPreview" class="code-editor-wrapper">
              <el-input v-model="aiGeneratedContent" type="textarea" :rows="10" class="content-editor" />
            </div>

            <!-- 预览模式 -->
            <div v-else class="generated-preview-box">
              <div class="preview-frame" v-html="aiGeneratedContent"></div>
            </div>
          </div>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="aiGenerateDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAiGenerate" :loading="aiGenerating">
          <el-icon>
            <MagicStick />
          </el-icon>
          开始生成
        </el-button>
        <el-button type="success" @click="applyAiContent" :disabled="!aiGeneratedContent">
          <el-icon>
            <Check />
          </el-icon>
          应用到模板
        </el-button>
      </template>
    </el-dialog>

    <!-- AI润色对话框 -->
    <el-dialog v-model="aiPolishDialogVisible" title="AI润色邮件模板" width="700px" :close-on-click-modal="false">
      <el-form ref="aiPolishForm" :model="aiPolishForm" label-width="100px">
        <el-form-item label="润色要求" prop="requirement">
          <el-input v-model="aiPolishForm.requirement" type="textarea" :rows="4"
            placeholder="请描述您希望如何优化，例如：&#10;让颜色更温馨，添加更多留白，让排版更现代化等（可选）" />
        </el-form-item>

        <el-form-item label="当前内容">
          <div class="current-content-box">
            <div class="current-content-toolbar">
              <el-tag size="small" type="info">当前模板预览</el-tag>
            </div>
            <div class="preview-frame" v-html="templateForm.content"></div>
          </div>
        </el-form-item>

        <el-form-item label="润色结果" v-if="aiPolishedContent">
          <div class="generated-content-box">
            <div class="generated-toolbar">
              <el-button size="small" @click="showPolishPreview = !showPolishPreview"
                :type="showPolishPreview ? 'warning' : 'info'">
                <el-icon>
                  <View />
                </el-icon>
                {{ showPolishPreview ? '查看HTML代码' : '可视化预览效果' }}
              </el-button>
            </div>

            <!-- 代码模式 -->
            <div v-if="!showPolishPreview" class="code-editor-wrapper">
              <el-input v-model="aiPolishedContent" type="textarea" :rows="10" class="content-editor" />
            </div>

            <!-- 预览模式 -->
            <div v-else class="generated-preview-box">
              <div class="preview-frame" v-html="aiPolishedContent"></div>
            </div>
          </div>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="aiPolishDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAiPolish" :loading="aiPolishing">
          <el-icon>
            <Brush />
          </el-icon>
          开始润色
        </el-button>
        <el-button type="success" @click="applyPolishedContent" :disabled="!aiPolishedContent">
          <el-icon>
            <Check />
          </el-icon>
          应用润色结果
        </el-button>
      </template>
    </el-dialog>

    <!-- 预览对话框 -->
    <el-dialog v-model="previewDialogVisible" title="邮件模板预览" width="800px" top="5vh">
      <div class="preview-container">
        <div class="preview-subject">
          <strong>邮件主题：</strong>{{ previewTemplate?.subject || '' }}
        </div>
        <div class="preview-note">
          <el-icon>
            <InfoFilled />
          </el-icon>
          这是邮件的实际显示效果预览
        </div>
        <div class="preview-frame-full" v-html="previewTemplate?.content || ''"></div>
      </div>
    </el-dialog>

    <!-- 测试对话框 -->
    <el-dialog v-model="testDialogVisible" title="测试邮件模板" width="500px" :close-on-click-modal="false">
      <el-form ref="testForm" :model="testForm" label-width="100px">
        <el-form-item label="邮箱账户" prop="account_id">
          <el-select v-model="testForm.account_id" placeholder="请选择邮箱账户" style="width: 100%" clearable>
            <el-option v-for="account in accountList" :key="account.id" :label="account.account_name"
              :value="account.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="收件人邮箱" prop="receiver_email">
          <el-input v-model="testForm.receiver_email" placeholder="请输入收件人邮箱地址" size="large" />
        </el-form-item>

        <el-form-item label="收件人名称">
          <el-input v-model="testForm.receiver_name" placeholder="请输入收件人名称（可选）" size="large" />
        </el-form-item>

        <el-form-item>
          <el-alert type="info" show-icon :closable="false">
            <template #title>
              <div>如果填写了收件人名称，邮件中将自动替换 {name} 变量</div>
            </template>
          </el-alert>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="testDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTest" :loading="testing">发送测试邮件</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Promotion, View, MagicStick, Brush, Check, InfoFilled
} from '@element-plus/icons-vue'

export default {
  name: 'EmailTemplate',
  components: {
    Search, Plus, Edit, Delete, Promotion, View, MagicStick, Brush, Check, InfoFilled
  },
  data() {
    return {
      loading: false,
      testing: false,
      dialogVisible: false,
      testDialogVisible: false,
      previewDialogVisible: false,
      dialogTitle: '新增邮件模板',
      isEditMode: false,
      currentTemplateId: null,
      accountList: [],
      showPreview: false,

      // AI生成相关
      aiGenerateDialogVisible: false,
      aiGenerating: false,
      showAiPreview: false,
      aiGeneratedContent: '',
      aiGenerateForm: {
        requirement: ''
      },

      // AI润色相关
      aiPolishDialogVisible: false,
      aiPolishing: false,
      showPolishPreview: false,
      aiPolishedContent: '',
      aiPolishForm: {
        requirement: ''
      },

      // 预览相关
      previewTemplate: null,

      // 提示词建议
      promptSuggestions: [
        '生成一个商务合作邀请邮件模板，简洁大方，包含公司介绍、合作意向、联系方式，使用蓝色和白色为主色调',
        '生成一个客户感谢信模板，温馨亲切，使用柔和的暖色调，表达诚挚的感谢',
        '生成一个促销活动通知模板，活泼有吸引力，使用红色和金色，突出优惠信息',
        '生成一个订单确认邮件模板，清晰明了，包含订单详情、支付信息、物流信息等',
        '生成一个会员注册欢迎邮件模板，友好热情，使用渐变色彩，引导用户了解服务',
        '生成一个会议邀请邮件模板，正式专业，包含会议时间、地点、议程等信息'
      ],

      searchForm: {
        keyword: ''
      },
      templateForm: {
        id: null,
        template_name: '',
        subject: '',
        content: '',
        status: 1,
        notes: ''
      },
      testForm: {
        account_id: null,
        receiver_email: '',
        receiver_name: ''
      },
      formRules: {
        template_name: [
          { required: true, message: '请输入模板名称', trigger: 'blur' }
        ],
        subject: [
          { required: true, message: '请输入邮件主题', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入邮件内容', trigger: 'blur' }
        ],
        account_id: [
          { required: true, message: '请选择邮箱账户', trigger: 'change' }
        ],
        receiver_email: [
          { required: true, message: '请输入收件人邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ]
      },
      templateList: [],
      userList: [],
      userMap: {},
      userLoading: false,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    previewContent() {
      if (!this.templateForm.content) return '';
      let content = this.templateForm.content;

      // 清理文档声明标签
      content = content.replace(/<!DOCTYPE[^>]*>/gi, '');
      content = content.replace(/<html[^>]*>/gi, '');
      content = content.replace(/<\/html>/gi, '');
      content = content.replace(/<head>[\s\S]*?<\/head>/gi, '');
      content = content.replace(/<body[^>]*>/gi, '');
      content = content.replace(/<\/body>/gi, '');

      // 移除空的div和td标签（AI可能生成的空白区域）
      content = content.replace(/<div[^>]*>\s*<\/div>/gi, '');
      content = content.replace(/<td[^>]*>\s*<\/td>/gi, '');
      content = content.replace(/<tr[^>]*>\s*<\/tr>/gi, '');

      // 移除过多的空行
      content = content.replace(/>\s+</g, '><');

      // 替换变量为示例值用于预览
      content = content.replace(/\{name\}/g, '<span style="color: #409EFF; font-weight: bold;">张三</span>');

      return content;
    }
  },
  mounted() {
    this.fetchTemplateList();
    this.fetchAccountList();
    this.fetchUserList();
  },
  methods: {
    // 获取账户列表
    async fetchAccountList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/email_account.php`,
          {
            action: 'list',
            store_id: this.userInfo.id,
            page: 1,
            page_size: 1000
          }
        );

        if (data.data) {
          this.accountList = data.data.filter(account => account.status === 1);
        }
      } catch (error) {
        console.error('获取邮箱账户列表错误:', error);
      }
    },

    // 获取用户列表
    async fetchUserList() {
      this.userLoading = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/business_card_management.php`,
          {
            action: 'get_users',
            store_id: this.userInfo.id
          }
        )

        if (response.data.data) {
          this.userList = response.data.data
          this.userMap = {}
          response.data.data.forEach(user => {
            this.userMap[user.id] = user
          })
        }
      } catch (error) {
        console.error('获取用户列表错误:', error)
      } finally {
        this.userLoading = false
      }
    },

    // 根据user_id获取用户信息
    getUserInfo(userId) {
      const user = this.userMap[userId]
      if (user) {
        return {
          nickname: user.nickname,
          phone: user.phone,
          avatar: user.avatar
        }
      }
      return {
        nickname: '管理员',
        phone: '',
        avatar: ''
      }
    },

    // 截断HTML内容用于列表预览
    truncateHtml(html, maxLength) {
      if (!html) return '';
      // 移除HTML标签获取纯文本
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const text = tempDiv.textContent || tempDiv.innerText || '';

      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    // 获取模板列表
    async fetchTemplateList() {
      this.loading = true;
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/email_template.php`,
          params
        );

        if (data.data) {
          this.templateList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取邮件模板列表失败');
        console.error('获取邮件模板列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchTemplateList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增邮件模板';
      this.isEditMode = false;
      this.showPreview = false;
      this.templateForm = {
        id: null,
        template_name: '',
        subject: '',
        content: '',
        status: 1,
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑邮件模板';
      this.isEditMode = true;
      this.showPreview = false;
      this.templateForm = {
        id: row.id,
        template_name: row.template_name,
        subject: row.subject,
        content: row.content,
        status: row.status,
        notes: row.notes
      };
      this.dialogVisible = true;
    },

    // 打开预览对话框
    openPreviewDialog(row) {
      this.previewTemplate = {
        subject: row.subject.replace(/\{name\}/g, '张三'),
        content: row.content.replace(/\{name\}/g, '张三')
      };
      this.previewDialogVisible = true;
    },

    // 打开测试对话框
    openTestDialog(row) {
      this.currentTemplateId = row.id;
      this.testForm = {
        account_id: null,
        receiver_email: '',
        receiver_name: ''
      };
      this.testDialogVisible = true;
    },

    // 打开AI生成对话框
    openAiGenerateDialog() {
      this.aiGenerateForm.requirement = '';
      this.aiGeneratedContent = '';
      this.showAiPreview = false;
      this.aiGenerateDialogVisible = true;
    },

    // 打开AI润色对话框
    openAiPolishDialog() {
      this.aiPolishForm.requirement = '';
      this.aiPolishedContent = '';
      this.showPolishPreview = false;
      this.aiPolishDialogVisible = true;
    },

    // AI生成模板
    async handleAiGenerate() {
      if (!this.aiGenerateForm.requirement) {
        this.$message.warning('请输入需求描述');
        return;
      }

      this.aiGenerating = true;
      this.aiGeneratedContent = '';
      this.showAiPreview = false;

      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/ai_config.php`,
          {
            action: 'generate_template',
            store_id: this.userInfo.id,
            requirement: this.aiGenerateForm.requirement,
            ai_action: 'generate'
          }
        );

        if (data.success) {
          this.aiGeneratedContent = data.data.content;
          this.$message.success('模板生成成功');
        } else {
          this.$message.error(data.error || '生成失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '生成失败';
        this.$message.error(`AI生成失败: ${errorMsg}`);
        console.error('AI生成错误:', error);
      } finally {
        this.aiGenerating = false;
      }
    },

    // AI润色模板
    async handleAiPolish() {
      if (!this.templateForm.content) {
        this.$message.warning('没有可润色的内容');
        return;
      }

      this.aiPolishing = true;
      this.aiPolishedContent = '';
      this.showPolishPreview = false;

      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/ai_config.php`,
          {
            action: 'generate_template',
            store_id: this.userInfo.id,
            requirement: this.aiPolishForm.requirement || '请优化样式和排版',
            html_content: this.templateForm.content,
            ai_action: 'polish'
          }
        );

        if (data.success) {
          this.aiPolishedContent = data.data.content;
          this.$message.success('润色完成');
        } else {
          this.$message.error(data.error || '润色失败');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '润色失败';
        this.$message.error(`AI润色失败: ${errorMsg}`);
        console.error('AI润色错误:', error);
      } finally {
        this.aiPolishing = false;
      }
    },

    // 应用AI生成的内容
    applyAiContent() {
      if (this.aiGeneratedContent) {
        this.templateForm.content = this.aiGeneratedContent;
        this.aiGenerateDialogVisible = false;
        this.$message.success('已应用AI生成的模板');
      }
    },

    // 应用AI润色的内容
    applyPolishedContent() {
      if (this.aiPolishedContent) {
        this.templateForm.content = this.aiPolishedContent;
        this.aiPolishDialogVisible = false;
        this.$message.success('已应用润色后的内容');
      }
    },

    // 提交表单（新增/编辑）
    submitTemplateForm() {
      this.$refs.templateForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/email_template.php`,
              {
                action,
                ...this.templateForm,
                store_id: this.userInfo.id
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchTemplateList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 提交测试
    submitTest() {
      this.$refs.testForm.validate(async (valid) => {
        if (valid) {
          this.testing = true;
          try {
            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/email_template.php`,
              {
                action: 'test',
                id: this.currentTemplateId,
                store_id: this.userInfo.id,
                ...this.testForm
              }
            );

            if (data.success) {
              this.$message.success('模板测试邮件发送成功');
              this.testDialogVisible = false;
            } else {
              this.$message.error(data.error || '测试失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '测试失败';
            this.$message.error(`测试失败: ${errorMsg}`);
            console.error('测试错误:', error);
          } finally {
            this.testing = false;
          }
        }
      });
    },

    // 删除模板
    async handleDelete(id) {
      this.$confirm('确定要删除该邮件模板吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/email_template.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchTemplateList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 0px;
}

.settings-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.content-preview {
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

// 邮件内容表单项目 - 关键：设置内容区域为垂直布局，左对齐
.content-form-item {
  :deep(.el-form-item__content) {
    display: flex;
    flex-direction: column;
    align-items: flex-start; // 左对齐
    margin-left: 0 !important; // 移除可能的左边距
  }
}

// 底部提示
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 12px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 14px;
  background: #f5f7fa;
  border-radius: 4px;
  border-left: 3px solid #409EFF;
  width: 100%;
  box-sizing: border-box;
}

// 编辑器工具栏容器
.editor-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  width: 100%;
  box-sizing: border-box;

  .toolbar-left {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .toolbar-right {
    display: flex;
    gap: 10px;
    align-items: center;
  }
}

// 代码编辑器包装
.code-editor-wrapper {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;

  .code-editor-header {
    padding: 10px 16px;
    background: #2c3e50;
    color: #ecf0f1;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid #34495e;
  }
}

// 内容编辑器
.content-editor {
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  line-height: 1.6;
}

// 预览框
.content-preview-box {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;

  .preview-toolbar {
    padding: 10px 16px;
    background: #f0f9eb;
    border-bottom: 1px solid #c2e7b0;
    display: flex;
    align-items: center;
    gap: 12px;

    .preview-hint {
      font-size: 12px;
      color: #67c23a;
    }
  }

  .preview-frame {
    padding: 20px;
    min-height: 200px;
    max-height: 500px;
    overflow-y: auto;
    background: #fff;
  }
}

// 预览容器
.preview-container {
  .preview-subject {
    padding: 12px 16px;
    background: #f5f7fa;
    border-bottom: 1px solid #e4e7ed;
    margin-bottom: 12px;
    border-radius: 4px;
    font-size: 14px;
  }

  .preview-note {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: #ecf5ff;
    border-left: 3px solid #409EFF;
    color: #409EFF;
    font-size: 12px;
    margin-bottom: 16px;
    border-radius: 0 4px 4px 0;
  }

  .preview-frame-full {
    padding: 20px;
    min-height: 300px;
    max-height: 600px;
    overflow-y: auto;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
  }
}

// AI生成对话框样式
.prompt-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start; // 添加：顶部对齐

  .prompt-tag {
    cursor: pointer;
    transition: all 0.3s;
    max-width: 100%;
    white-space: normal; // 允许换行
    line-height: 1.6; // 增加行高
    padding: 8px 14px; // 增加内边距
    height: auto !important; // 关键：覆盖 element-plus 的固定高度
    word-break: break-word; // 长单词换行

    // 覆盖 el-tag 内部样式
    :deep(.el-tag__content) {
      white-space: normal;
      line-height: 1.6;
      display: block; // 块级显示
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

.generated-content-box {
  width: 100%;

  .generated-toolbar {
    margin-bottom: 10px;
  }

  .content-editor {
    font-family: 'Courier New', Courier, monospace;
    font-size: 13px;
    line-height: 1.5;
  }

  .generated-preview-box {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    width: 100%;

    .preview-frame {
      padding: 20px;
      min-height: 200px;
      max-height: 500px;
      overflow-y: auto;
      background: #fff;
    }
  }
}

.current-content-box {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  width: 100%;

  .current-content-toolbar {
    padding: 8px 12px;
    background: #f5f7fa;
    border-bottom: 1px solid #e4e7ed;
  }

  .preview-frame {
    padding: 16px;
    max-height: 300px;
    overflow-y: auto;
    background: #fafafa;
  }
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

.user-info {
  .user-nickname {
    font-weight: 500;
    margin-bottom: 4px;
  }

  .user-phone {
    color: #909399;
    font-size: 12px;
  }
}
</style>